import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  TaskFitColumn,
  TaskFitDimension,
  TaskFitRow,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { Table } from '../../../components/table/table';
import type { TableColumn } from '../../../components/table/table';
import {
  inkDarkest,
  inkLight,
  primaryBase,
  primaryLightest,
  redLightest,
  skyLighter,
  yellowLightest,
} from '../../../styles/design-tokens';
import { Text } from '../../../ui-library/typography/typography';
import { EM_DASH } from '../model-comparison/model-comparison.helpers';
import {
  buildMatrixRows,
  computeBestByTaskType,
  formatMetricValue,
  formatReworkRange,
  getCell,
  metricCellValue,
  resolveMetricCellColors,
  resolveRowLabel,
  sortMatrixRows,
} from './task-fit.helpers';
import type { MatrixRow, TaskFitMetric } from './task-fit.type';

type Props = {
  rows: TaskFitRow[];
  /** The VISIBLE model-family columns (already narrowed by the tab's filters). */
  columns: TaskFitColumn[];
  dimension: TaskFitDimension;
  /** meta.min_cell_tasks — the sample-size floor shown in the suppressed tooltip. */
  minCellTasks: number;
  /** The per-cell metric the heatmap colors and sorts by (default rework). */
  metric?: TaskFitMetric;
  /** Called with (rowKey, modelFamily) when a populated cell is clicked. */
  onSelectCell?: (rowKey: string, modelFamily: string) => void;
};

/**
 * The Task Fit matrix, TRANSPOSED: one row per model family (left, alphabetized)
 * and one column per task type (top, kept in the payload's volume order and
 * suffixed with its linked-task count), each cell the pairing's value for the
 * active metric (rework rate, median cycle time, or cost per completed task) on
 * a LOWER-IS-BETTER scale — rework tones absolutely, the other metrics relative
 * to the shown values. Under the rework metric the confidently-best VISIBLE
 * model per task-type COLUMN is ringed — ranked by Wilson interval upper bound
 * and only when separated from every rival (see computeBestByTaskType),
 * recomputed on the frontend from the currently-shown models so the ring
 * follows the active filters; the other metrics carry no confidence intervals
 * and never ring. Cells below the sample threshold are greyed with an
 * explanatory tooltip; pairings with no shown value render an em-dash; a
 * populated cell is clickable to open the tasks-behind-the-cell drill-down.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The colored-cell matrix with a legend and caption
 */
export const TaskFitMatrix = ({ rows, columns, dimension, minCellTasks, metric = 'rework', onSelectCell }: Props) => {
  const { t } = useTranslation();

  const matrixRows = useMemo(() => buildMatrixRows(columns, rows), [columns, rows]);
  const bestByTaskType = useMemo(
    () => (metric === 'rework' ? computeBestByTaskType(columns, rows) : new Map<string, string>()),
    [columns, rows, metric],
  );
  // Every shown value for the active metric across the visible matrix, feeding
  // the relative tone scale for the cycle-time and cost views.
  const visibleFamilies = useMemo(() => new Set(columns.map((column) => column.model_family)), [columns]);
  const shownValues = useMemo(
    () =>
      rows.flatMap((row) =>
        row.cells
          .filter((cell) => visibleFamilies.has(cell.model_family))
          .map((cell) => metricCellValue(cell, metric))
          .filter((value): value is number => value !== null),
      ),
    [rows, visibleFamilies, metric],
  );

  const tableColumns = useMemo<TableColumn<MatrixRow>[]>(() => {
    const labelColumn: TableColumn<MatrixRow> = {
      key: 'model_family',
      label: t('agent_miner.task_fit.model'),
      width: '180px',
      // Sorts the model rows alphabetically (also the default state on load).
      sortable: true,
      render: (_, row) => <RowLabel>{row.model_family}</RowLabel>,
    };

    const taskTypeColumns: TableColumn<MatrixRow>[] = rows.map((row) => ({
      key: row.row_key as keyof MatrixRow,
      label: (
        <ColumnHeader>
          {row.linked_tasks != null
            ? `${resolveRowLabel(row, dimension)} (${row.linked_tasks})`
            : resolveRowLabel(row, dimension)}
        </ColumnHeader>
      ),
      width: '120px',
      // Sorts the model rows by this task type's active metric (see sortMatrixRows).
      sortable: true,
      render: (_, matrixRow) => (
        <MatrixCell
          cell={getCell(matrixRow, row.row_key)}
          metric={metric}
          shownValues={shownValues}
          isBest={bestByTaskType.get(row.row_key) === matrixRow.model_family}
          minCellTasks={minCellTasks}
          onSelect={onSelectCell ? () => onSelectCell(row.row_key, matrixRow.model_family) : undefined}
        />
      ),
    }));

    return [labelColumn, ...taskTypeColumns];
  }, [rows, dimension, bestByTaskType, minCellTasks, metric, shownValues, onSelectCell, t]);

  return (
    <Wrapper data-testid="task-fit-matrix">
      <TableArea>
        <Table<MatrixRow>
          data={matrixRows}
          columns={tableColumns}
          getRowId={(row) => row.model_family}
          totalItems={matrixRows.length}
          // Default: model name ascending (alphabetical). Task-type headers
          // re-sort by the active metric; nulls/suppressed always sink to the bottom.
          defaultSortedBy="model_family"
          defaultSortedByDirection="asc"
          customSort={(data, sortBy, direction) => sortMatrixRows(data, sortBy, direction, metric)}
          stickyHeader
        />
      </TableArea>
      <Legend metric={metric} />
    </Wrapper>
  );
};

type MatrixCellProps = {
  cell: ReturnType<typeof getCell>;
  metric: TaskFitMetric;
  /** Every shown value for the metric in the visible matrix (relative tones). */
  shownValues: number[];
  /** Whether this cell is the confidently-best VISIBLE model in its task-type column. */
  isBest: boolean;
  minCellTasks: number;
  /** Opens the tasks drill-down for this cell; absent leaves the cell inert. */
  onSelect?: () => void;
};

/**
 * Renders a single matrix cell: a tinted metric value over its sample size, a
 * greyed suppressed cell with a sample-size tooltip, or an em-dash for a
 * pairing with no shown value. The confidently-best cell in a column is ringed
 * (rework metric only), and a populated cell is a button opening the cell's
 * tasks drill-down.
 *
 * @param {MatrixCellProps} props - Component props
 * @returns {JSX.Element} The rendered cell
 */
const MatrixCell = ({ cell, metric, shownValues, isBest, minCellTasks, onSelect }: MatrixCellProps) => {
  const { t } = useTranslation();

  if (!cell) {
    return (
      <CellBox as="span" $color={inkLight}>
        {EM_DASH}
      </CellBox>
    );
  }

  if (cell.suppressed) {
    return (
      <Tooltip
        label={t('agent_miner.task_fit.suppressed_tooltip', { n: cell.sample_size, min: minCellTasks })}
        position="top"
        withArrow
        events={{ hover: true, focus: true, touch: true }}
      >
        <CellBox
          as={onSelect ? 'button' : 'span'}
          type={onSelect ? 'button' : undefined}
          onClick={onSelect}
          $background={skyLighter}
          $color={inkLight}
          $cursor={onSelect ? 'pointer' : 'help'}
        >
          {EM_DASH}
        </CellBox>
      </Tooltip>
    );
  }

  const value = metricCellValue(cell, metric);
  if (value === null) {
    return (
      <CellBox as="span" $color={inkLight}>
        {EM_DASH}
      </CellBox>
    );
  }

  const { background, color } = resolveMetricCellColors(value, metric, shownValues);
  // Rework rates are proportions of transitioned tasks; the other metrics are
  // computed over completed tasks — the subtext shows the matching denominator.
  const sampleSize = metric === 'rework' ? cell.sample_size : cell.completed_tasks;
  // Show the Wilson interval so a wide (small-sample) rate reads as uncertain.
  const reworkRange = metric === 'rework' ? formatReworkRange(cell) : null;
  const tooltipLabel = reworkRange
    ? t('agent_miner.task_fit.cell_tooltip_with_range', {
        n: cell.sample_size,
        completed: cell.completed_tasks,
        range: reworkRange,
      })
    : t('agent_miner.task_fit.cell_tooltip', { n: cell.sample_size, completed: cell.completed_tasks });

  return (
    <Tooltip label={tooltipLabel} position="top" withArrow events={{ hover: true, focus: true, touch: true }}>
      <CellBox
        as={onSelect ? 'button' : 'span'}
        type={onSelect ? 'button' : undefined}
        onClick={onSelect}
        data-best={isBest ? 'true' : undefined}
        $background={background}
        $color={color}
        $bold
        $cursor={onSelect ? 'pointer' : undefined}
        $isBest={isBest}
      >
        <span>{formatMetricValue(value, metric)}</span>
        {sampleSize > 0 && <CellSubtext>{`n=${sampleSize}`}</CellSubtext>}
      </CellBox>
    </Tooltip>
  );
};

/**
 * Color-key strip beneath the matrix. The rework metric spells out its absolute
 * tone scale and the confidently-best ring; the relative metrics (cycle time,
 * cost) use generic better/middle/worse labels and no ring entry.
 *
 * @param {{ metric: TaskFitMetric }} props - Component props
 * @returns {JSX.Element} The legend row
 */
const Legend = ({ metric }: { metric: TaskFitMetric }) => {
  const { t } = useTranslation();
  const isRework = metric === 'rework';

  return (
    <LegendRow data-testid="task-fit-legend">
      <LegendItem>
        <Swatch $background={primaryLightest} />
        <Text size="tiny" color={inkLight}>
          {isRework ? t('agent_miner.task_fit.legend_low') : t('agent_miner.task_fit.legend_better')}
        </Text>
      </LegendItem>
      <LegendItem>
        <Swatch $background={yellowLightest} />
        <Text size="tiny" color={inkLight}>
          {isRework ? t('agent_miner.task_fit.legend_medium') : t('agent_miner.task_fit.legend_middle')}
        </Text>
      </LegendItem>
      <LegendItem>
        <Swatch $background={redLightest} />
        <Text size="tiny" color={inkLight}>
          {isRework ? t('agent_miner.task_fit.legend_high') : t('agent_miner.task_fit.legend_worse')}
        </Text>
      </LegendItem>
      <LegendItem>
        <Swatch $background={skyLighter} />
        <Text size="tiny" color={inkLight}>
          {t('agent_miner.task_fit.legend_suppressed')}
        </Text>
      </LegendItem>
      {isRework && (
        <LegendItem>
          <Swatch $background="transparent" $ring />
          <Text size="tiny" color={inkLight}>
            {t('agent_miner.task_fit.legend_best')}
          </Text>
        </LegendItem>
      )}
    </LegendRow>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 0;
`;

const TableArea = styled.div`
  flex: 1;
  min-height: 0;
`;

const RowLabel = styled.span`
  color: ${inkDarkest};
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ColumnHeader = styled.span`
  white-space: nowrap;
`;

type CellBoxProps = {
  /** Cell fill; defaults to transparent so an em-dash cell recedes. */
  $background?: string;
  /** Text color; defaults to inherit. */
  $color?: string;
  /** Pointer for a clickable cell, help for a suppressed cell with no handler, or unset when inert. */
  $cursor?: 'pointer' | 'help';
  /** Bolds the value text for a populated (non-suppressed) cell. */
  $bold?: boolean;
  /** Rings the cell with the confidently-best inset border. */
  $isBest?: boolean;
};

const CellBox = styled.button<CellBoxProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  width: 100%;
  min-height: 40px;
  /* The global reset only sets border-box on <button>, not <span> — without
     this, the as="span" branches (used when onSelect is undefined) grow past
     the column via content-box padding math instead of matching the button. */
  box-sizing: border-box;
  border: none;
  background: ${(props) => props.$background ?? 'transparent'};
  color: ${(props) => props.$color ?? 'inherit'};
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 13px;
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  font-weight: ${(props) => (props.$bold ? 600 : 'inherit')};
  cursor: ${(props) => props.$cursor ?? 'inherit'};
  box-shadow: ${(props) => (props.$isBest ? `inset 0 0 0 2px ${primaryBase}` : 'none')};
`;

const CellSubtext = styled.span`
  font-size: 10px;
  font-weight: 500;
  opacity: 0.75;
  line-height: 1;
`;

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  flex: 0 0 auto;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Swatch = styled.span<{ $background: string; $ring?: boolean }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background: ${(props) => props.$background};
  box-shadow: ${(props) => (props.$ring ? `inset 0 0 0 2px ${primaryBase}` : 'none')};
`;
